import { useCallback, useEffect, useState } from "react";
import { isCapacitor } from "../../lib/capacitor";
import { enablePushNotifications } from "../../lib/pwa";
import { markeerGevraagd, openAppInstellingen, vraagBatterijVrijstelling } from "../../lib/meldingen";
import { magVragen, volledigeStand, vraagRecht, type VolledigeStand } from "../../lib/rechten";
import { pluginNamen } from "../../lib/nativeplugin";

/**
 * Alles wat de app van Android nodig heeft, met de stand erbij.
 *
 * Waarom dit een scherm is en niet alleen een vraag bij de eerste start: rechten
 * verdwijnen zonder dat iemand iets doet. Een verwijdering van de app wist ze
 * allemaal, en Android trekt ze vanzelf in bij een app die je maanden niet
 * opent. Je merkt dat pas als je de microfoonknop indrukt en er gebeurt niets -
 * of, bij meldingen, helemaal niet.
 *
 * Het toont ook wat goed staat. De vraag "staat het nou goed?" verdient een
 * antwoord dat je kunt zien, niet de afwezigheid van een waarschuwing.
 *
 * En het toont altijd iets. Een stand die niet te lezen was is ook een stand:
 * dan staat er wat er misging en kun je het alsnog proberen. Dit scherm bleef
 * eerst op "Laden..." hangen als de Android-kant niet antwoordde.
 */
export default function RechtenTab() {
  const [stand, setStand] = useState<VolledigeStand | null>(null);
  const [bezig, setBezig] = useState<string | null>(null);

  const lees = useCallback(async () => {
    setStand(await volledigeStand());
  }, []);

  useEffect(() => {
    void lees();
  }, [lees]);

  /* Terug uit een systeemdialoog: opnieuw kijken wat de gebruiker koos. De
     dialoog zelf vertelt ons niets. */
  useEffect(() => {
    const opnieuw = () => {
      if (document.visibilityState === "visible") void lees();
    };
    document.addEventListener("visibilitychange", opnieuw);
    window.addEventListener("focus", opnieuw);
    return () => {
      document.removeEventListener("visibilitychange", opnieuw);
      window.removeEventListener("focus", opnieuw);
    };
  }, [lees]);

  async function doe(naam: string, actie: () => Promise<unknown>) {
    setBezig(naam);
    try {
      await actie();
    } finally {
      setBezig(null);
      void lees();
    }
  }

  if (!stand) {
    return <div className="px-4 py-4 text-sm text-slate-400">Laden…</div>;
  }

  const { melding, rechten, rechtenFout } = stand;
  const permissieOk = melding.permissie === "granted";
  const permissieGeweigerd = melding.permissie === "denied";
  const permissieOnbekend = melding.permissie === "onbekend";

  /* Batterijbeheer bestaat alleen in de Android-app. `null` betekent dat we het
     niet konden lezen - dan tonen we de regel juist wél, met de reden erbij en
     de knop eronder, want vragen kan altijd. */
  const toontBatterij = isCapacitor() && (melding.batterij === null || melding.batterij.supported);
  const batterijOk = melding.batterij?.ignoring === true;

  const ietsOnbekend =
    permissieOnbekend ||
    melding.batterij === null ||
    rechten.microfoon === "onbekend" ||
    rechten.locatie === "onbekend";

  return (
    <div className="space-y-3 px-4 py-3">
      <p className="text-xs text-slate-400">
        Wat Swift van Android nodig heeft. Ontbreekt er iets, dan werkt dat ene
        onderdeel niet — meestal zonder foutmelding.
      </p>

      <Regel
        titel="Meldingen"
        waarvoor="Berichten zien als de app dicht is"
        stand={permissieOk ? "goed" : permissieOnbekend ? "onbekend" : "mis"}
        uitleg={
          permissieOk
            ? "Swift mag meldingen tonen."
            : permissieOnbekend
            ? "Niet te lezen — probeer het opnieuw."
            : permissieGeweigerd
            ? "Geweigerd. Android laat dit alleen terugdraaien in de instellingen van de app zelf."
            : "Nog niet gevraagd."
        }
        fout={melding.permissieFout}
        knop={permissieOk ? null : permissieGeweigerd ? "Naar instellingen" : "Aanzetten"}
        bezig={bezig === "melding"}
        onClick={() =>
          void doe("melding", async () => {
            if (permissieGeweigerd) return openAppInstellingen();
            markeerGevraagd();
            return enablePushNotifications();
          })
        }
      />

      {toontBatterij && (
        <Regel
          titel="Buiten het batterijbeheer"
          waarvoor="Meldingen die meteen aankomen, ook na stille dagen"
          stand={batterijOk ? "goed" : melding.batterij === null ? "onbekend" : "mis"}
          uitleg={
            batterijOk
              ? "Android houdt Swift niet tegen."
              : melding.batterij === null
              ? "Niet te lezen. Je kunt het wel gewoon aanzetten."
              : "Android zet Swift in slaap als je hem een paar dagen niet opent. Meldingen komen dan uren te laat, of helemaal niet."
          }
          fout={melding.batterijFout}
          knop={batterijOk ? null : "Toestaan"}
          bezig={bezig === "batterij"}
          onClick={() => void doe("batterij", vraagBatterijVrijstelling)}
        />
      )}

      {isCapacitor() && (
        <>
          <RechtRegel
            titel="Microfoon"
            waarvoor="Spraakberichten opnemen"
            stand={rechten.microfoon}
            fout={rechtenFout}
            bezig={bezig === "microfoon"}
            onVraag={() => void doe("microfoon", () => vraagRecht("microfoon"))}
            onInstellingen={() => void doe("microfoon", openAppInstellingen)}
          />
          <RechtRegel
            titel="Locatie"
            waarvoor="Je plek delen in een gesprek"
            stand={rechten.locatie}
            fout={rechtenFout}
            bezig={bezig === "locatie"}
            onVraag={() => void doe("locatie", () => vraagRecht("locatie"))}
            onInstellingen={() => void doe("locatie", openAppInstellingen)}
          />
        </>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-xs text-slate-500">
        <button onClick={() => void lees()} className="underline hover:text-slate-300">
          Opnieuw kijken
        </button>
        {isCapacitor() && (
          <button
            onClick={() => void openAppInstellingen()}
            className="underline hover:text-slate-300"
          >
            Android-instellingen van Swift
          </button>
        )}
      </div>

      {isCapacitor() && (
        <div className="text-xs text-slate-500">
          Komen meldingen ondanks het bovenstaande niet aan, dan heeft je toestel
          er zijn eigen regels bovenop — Samsung noemt het “Apps in slaapstand”,
          Xiaomi en OnePlus hebben een autostart-lijst. Daar is geen knop voor;
          die staan op de pagina hierboven.
        </div>
      )}

      {/* Alleen zichtbaar als er iets niet te lezen was. Zonder deze lijst is
          "geen antwoord" niet te duiden: je weet dan niet of de plugin ontbreekt
          of dat hij zwijgt. Op een telefoon zonder usb-kabel is dit het enige
          spoor dat je kunt doorgeven. */}
      {isCapacitor() && ietsOnbekend && (
        <div className="break-words border-t border-slate-800 pt-2 font-mono text-[10px] text-slate-600">
          brug kent: {pluginNamen().join(", ") || "niets"}
        </div>
      )}
    </div>
  );
}

/** Een runtime-recht van Android. Het verschil tussen "nooit gevraagd" en
 *  "geweigerd" bepaalt wat er te doen valt: het eerste kun je gewoon vragen,
 *  voor het tweede toont Android geen dialoog meer. */
function RechtRegel({
  titel,
  waarvoor,
  stand,
  fout,
  bezig,
  onVraag,
  onInstellingen,
}: {
  titel: string;
  waarvoor: string;
  stand: string;
  fout: string | null;
  bezig: boolean;
  onVraag(): void;
  onInstellingen(): void;
}) {
  const ok = stand === "granted";
  const onbekend = stand === "onbekend";
  const kanVragen = magVragen(stand as never);

  return (
    <Regel
      titel={titel}
      waarvoor={waarvoor}
      stand={ok ? "goed" : onbekend ? "onbekend" : "mis"}
      uitleg={
        ok
          ? "Toegestaan."
          : onbekend
          ? "Niet te lezen."
          : kanVragen
          ? "Nog nooit gevraagd. Android toont dit als “Niet toegestaan”, net als een weigering — maar je hebt hier niets geweigerd."
          : "Geweigerd. Android toont die vraag niet meer; het kan alleen nog in de instellingen van de app."
      }
      fout={fout}
      knop={ok ? null : onbekend ? null : kanVragen ? "Toestaan" : "Naar instellingen"}
      bezig={bezig}
      onClick={kanVragen ? onVraag : onInstellingen}
    />
  );
}

function Regel({
  titel,
  waarvoor,
  stand,
  uitleg,
  fout,
  knop,
  bezig,
  onClick,
}: {
  titel: string;
  waarvoor: string;
  stand: "goed" | "mis" | "onbekend";
  uitleg: string;
  fout: string | null;
  knop: string | null;
  bezig: boolean;
  onClick(): void;
}) {
  const teken = stand === "goed" ? "✓" : stand === "onbekend" ? "?" : "!";
  const kleur =
    stand === "goed"
      ? "text-brand-green"
      : stand === "onbekend"
      ? "text-slate-500"
      : "text-amber-400";

  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded border border-slate-700 px-3 py-2">
      <span className={`mt-0.5 shrink-0 text-sm ${kleur}`} aria-hidden="true">
        {teken}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-200">{titel}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-600">{waarvoor}</div>
        <div className="mt-0.5 text-xs text-slate-500">{uitleg}</div>
        {/* De ruwe reden erbij. Lelijk, maar het is het enige spoor dat een
            gebruiker zonder usb-kabel ons kan doorgeven. */}
        {fout && (
          <div className="mt-1 break-words font-mono text-[10px] text-slate-600">{fout}</div>
        )}
      </div>
      {knop && (
        <button
          onClick={onClick}
          disabled={bezig}
          className="shrink-0 rounded bg-accent px-3 py-2 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {bezig ? "…" : knop}
        </button>
      )}
    </div>
  );
}
